<script setup>
import {ref, onMounted, computed} from 'vue'
import axios from "axios";
import moment from "moment";
import { reportLoadError } from "../helpers/network";

const props = defineProps({
  experienceId: {
    type: [String, Number],
  }
})

const data = ref({})

onMounted(async () => {

  try {
    const weather = await axios.get(`/wp-json/capes/weather/forecast/weather`, {
      params: {
        experience_id: props.experienceId,
      },
    });

    data.value = weather.data;
  } catch (error) {
    reportLoadError(error);
  }

})

const open = computed(() => {
  if (!data.value?.hours){
    return false;
  }

  if (!data.value.hours?.open || !data.value.hours?.close){
    return false;
  }

  const currentTime = moment();
  const todayOpen = moment(data.value.hours.open, "HH:mm:ss");
  const todayClose = moment(data.value.hours.close, "HH:mm:ss");

  return currentTime.isBetween(todayOpen, todayClose)
})

const issued_at = computed(() => {
  if (!data.value?.issued_at){
    return false;
  }

  return moment(data.value?.issued_at, "YYYY-MM-DD HH:mm:ss").format('h:mm a Do MMM YY');
})


const current = computed(() => {
  if (!data.value?.temperature){
    return false;
  }

  if (!data.value?.location.name){
    return false;
  }

  return `Currently ${parseInt(data.value?.temperature)}ºc in ${data.value?.location.name}`;
})

const description = computed(() => {
  if (!data.value?.description){
    return false;
  }

  return data.value.description;
})

const code = computed(() => {
  if (!data.value?.code){
    return false;
  }

  return data.value.code;
})

const icon = computed(() => {
  if (!data.value?.icon){
    return false;
  }

  return data.value.icon;
})

</script>

<template>
  <section class="relative px-4 py-6 lg:p-6 rounded-2xl mb-6 bg-white font-regular overflow-hidden">
    <span v-if="open" class="absolute top-0 right-0 z-10 inline-flex rounded-tr-2xl lg:px-3 px-4 lg:py-2 py-3 bg-secondary uppercase text-sm lg:text-mdx text-light font-medium tracking-widest">
        <span class="relative top-0.5">
            we are open!
        </span>
    </span>

    <span class="flex justify-center items-center w-14 h-14 rounded-full mb-5 lg:mb-6 bg-secondary text-[#F4F1EB]" :title="code">
      <span v-if="icon"
            v-html="icon"></span>
      <svg v-else width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M8.24561 4.4375V1.4375" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M3.74561 8.9375H0.745605" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M11.4277 5.75552L13.5487 3.63452" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M5.06338 5.75552L2.94238 3.63452" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M4.49573 11.4215C3.60226 10.0537 3.52032 8.30911 4.28166 6.86358C5.04299 5.41805 6.52799 4.49871 8.16133 4.46173C9.79468 4.42475 11.3198 5.27594 12.1457 6.68553" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M20.2457 18.6345C22.6485 17.252 23.7915 14.4035 23.0106 11.7436C22.2296 9.08372 19.7282 7.3051 16.9594 7.441C14.1906 7.57689 11.8754 9.59191 11.3587 12.3155C9.96987 11.6497 8.31506 11.9021 7.18784 12.9515C6.06062 14.001 5.69087 15.6336 6.2559 17.0664C6.82093 18.4992 8.20553 19.4399 9.74568 19.4375" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M11.2456 13.4376C11.2456 13.6883 11.2613 13.9388 11.2926 14.1876C11.2613 13.9388 11.2456 13.6883 11.2456 13.4376C11.247 13.0608 11.2849 12.685 11.3586 12.3156" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M10.8706 23.1875C11.0777 23.1875 11.2456 23.3554 11.2456 23.5625" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M10.4956 23.5625C10.4956 23.3554 10.6635 23.1875 10.8706 23.1875" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M10.8706 23.9375C10.6635 23.9375 10.4956 23.7696 10.4956 23.5625" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M11.2456 23.5625C11.2456 23.7696 11.0777 23.9375 10.8706 23.9375" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M15.3706 23.1875C15.5777 23.1875 15.7456 23.3554 15.7456 23.5625" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M14.9956 23.5625C14.9956 23.3554 15.1635 23.1875 15.3706 23.1875" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M15.3706 23.9375C15.1635 23.9375 14.9956 23.7696 14.9956 23.5625" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M15.7456 23.5625C15.7456 23.7696 15.5777 23.9375 15.3706 23.9375" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M13.1206 19.4375C13.3277 19.4375 13.4956 19.6054 13.4956 19.8125" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M12.7456 19.8125C12.7456 19.6054 12.9135 19.4375 13.1206 19.4375" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M13.1206 20.1875C12.9135 20.1875 12.7456 20.0196 12.7456 19.8125" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M13.4956 19.8125C13.4956 20.0196 13.3277 20.1875 13.1206 20.1875" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M17.6206 19.4375C17.8277 19.4375 17.9956 19.6054 17.9956 19.8125" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M17.2456 19.8125C17.2456 19.6054 17.4135 19.4375 17.6206 19.4375" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M17.6206 20.1875C17.4135 20.1875 17.2456 20.0196 17.2456 19.8125" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M17.9956 19.8125C17.9956 20.0196 17.8277 20.1875 17.6206 20.1875" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </span>

    <h4 class="mb-5 lg:mb-6 lg:text-xlx" v-if="current" v-text="current"></h4>

    <p class="mb-5 lg:mb-6 text-base lg:text-lg font-light" v-if="description" v-text="description"></p>

    <p v-if="issued_at"><small class="text-sm lg:text-base">Last updated: {{issued_at}}</small></p>
  </section>
</template>
